import { Injectable } from '@angular/core';
import { Document, FieldDataType, SchemaField } from '../../../core/store/models/document.model';
import { UtilService } from '../util.service';
import { DistanceService } from 'src/app/modules/core/services/distance/distance.service';
import numeral from 'numeral';
import moment from 'moment';
import { Layer } from '../../store/cop/models';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { TranslateService } from '@ngx-translate/core';
import { CultureService } from 'src/app/modules/core/services/culture.service';

@Injectable({
  providedIn: 'root'
})
export class DataFormattingService {
  constructor(
    private utilService: UtilService,
    private distanceService: DistanceService,
    private cultureService: CultureService
  ) {}

  public buildAddressFromFields(document: Document): string[] {
    let address1 = '';
    let address2 = '';

    if (!!document.address_1) {
      address1 += ` ${document.address_1}`;
    }
    if (!!document.address_2) {
      address1 += ` ${document.address_2}`;
    }

    if (!!document.city) {
      address2 += `${document.city}`;
    }
    if (!!document.state) {
      address2 += `, ${document.state}`;
    } else if (!!document.state_province_region) {
      address2 += `, ${document.state_province_region}`;
    }
    if (!!document.zip_postal_code) {
      address2 += ` ${document.zip_postal_code}`;
    }
    if (!!document.country) {
      address2 += ` ${document.country}`;
    }

    return [address1, address2];
  }

  public getLatLngForDisplay(geometry: any) {
    if (!geometry) {
      return null;
    }

    let data = null;
    let lat = numeral(0);
    let lng = numeral(0);

    try {
      data = JSON.parse(geometry);

      const normalizedData = this.utilService.getNormalizedGeometriesOrFeaturesFromGeoJson(data);
      const bounds = this.distanceService.getBoundsForGeometryFeatures(normalizedData);
      if (!bounds) {
        return null;
      }

      const center = bounds.getCenter();
      lat = numeral(center.lat());
      lng = numeral(center.lng());
    } catch {
      const geoString = geometry as string;
      if (geoString.startsWith('POINT')) {
        // assume WKT format point
        const startParen = geoString.indexOf('(');
        const endParen = geoString.indexOf(')');
        const latLngData = geoString.substring(startParen + 1, endParen);
        const latLngDataPairs = latLngData.split(' ');
        lng = numeral(latLngDataPairs[0]);
        lat = numeral(latLngDataPairs[1]);
      }
    }

    return `${lat.format('0[.]0000')}, ${lng.format('0[.]0000')}`;
  }

  public getLocationForDisplay(layer: Layer, dataset: Dataset, document: Document): string {
    let geoField: SchemaField = null;

    let geoFieldName = layer?.displayOptions?.geoFieldsToDisplay?.length > 0 ? layer?.displayOptions?.geoFieldsToDisplay[0] : null;
    if (!!geoFieldName) {
      geoField = dataset.schema.fields.find(f => f.systemName === geoFieldName);
    }

    // if we have not found the geo field then default to the first geometry in the schema
    if (!!!geoField) {
      geoField = dataset.schema.fields.find(f => f.dataType === FieldDataType.Geometry);
    }

    if (!!!geoField) {
      return '';
    }

    const geoValue = document[geoField.systemName];

    const location = this.getLatLngForDisplay(geoValue);

    return location;
  }

  public getDocumentLatLngForDisplay(document: Document) {
    if (!document) {
      return null;
    }

    const lat = numeral(document.latitude);
    const lng = numeral(document.longitude);

    return `${lat.format('0[.]0000')}, ${lng.format('0[.]0000')}`;
  }

  public getAnyDisplayName(names: string[], document: Document): string {
    for (let name of names) {
      if (!!document[name] && document[name].trim().length) {
        return document[name];
      }
    }

    return null;
  }

  public convertToMoment(dateString): moment.Moment {
    return moment(dateString);
  }

  public getPostedDateDisplay(postedDate: moment.Moment, useShortenedFormat: boolean = true): string {
    const format = useShortenedFormat ? 'LONG-DATE-WITH-TIME' : 'LONG-DOW-MONTH-DAY-YEAR-WITH-TIME';
    return this.cultureService.getDateDisplay(postedDate, format);
  }
}
